/* <collection-page>: filters + sorting without a page reload.
   Every change builds the URL from the filter form (Shopify storefront
   filter params), fetches this section through the Section Rendering API,
   swaps results, chips, count and the facet lists, and pushes the URL so it
   stays shareable and back/forward work. Without JS the form submits. */
class CollectionPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.form = this.querySelector('[data-filter-form]');
    this.timer = null;
    this.controller = null;

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-sort]')) { this.update(); return; }
      if (this.form && this.form.contains(e.target)) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(), e.target.type === 'number' ? 500 : 0);
      }
    });
    this.addEventListener('submit', (e) => {
      if (e.target === this.form) { e.preventDefault(); clearTimeout(this.timer); this.update(); }
    });
    this.addEventListener('click', (e) => {
      const link = e.target.closest('[data-filter-link], .pagination a');
      if (!link) return;
      e.preventDefault();
      this.load(link.href, { scroll: !!link.closest('.pagination') });
    });
    window.addEventListener('popstate', () => this.load(location.href, { push: false }));
  }

  buildUrl() {
    const url = new URL(this.dataset.url, location.origin);
    const params = new URLSearchParams();
    if (this.form) {
      new FormData(this.form).forEach((v, k) => { if (v !== '') params.append(k, v); });
    }
    const sort = this.querySelector('[data-sort]');
    if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
    url.search = params.toString();
    return url.href;
  }

  update() { this.load(this.buildUrl()); }

  async load(href, { push = true, scroll = false } = {}) {
    const results = this.querySelector('[data-collection-results]');
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    results?.setAttribute('aria-busy', 'true');
    try {
      const fetchUrl = new URL(href, location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const res = await fetch(fetchUrl.href, { signal: this.controller.signal });
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const swap = (sel) => {
        const next = html.querySelector(sel);
        const cur = this.querySelector(sel);
        if (next && cur) cur.replaceWith(next);
        else if (next && !cur) this.querySelector('[data-collection-toolbar]')?.after(next);
        else if (!next && cur) cur.remove();
      };
      swap('[data-collection-results]');
      swap('[data-collection-chips]');
      const facets = html.querySelector('[data-collection-filters]');
      const cur = this.querySelector('[data-collection-filters]');
      if (facets && cur) {
        // keep the open/closed state of each group
        const open = [...cur.querySelectorAll('details')].map((d) => d.open);
        cur.replaceWith(facets);
        facets.querySelectorAll('details').forEach((d, i) => { if (open[i] !== undefined) d.open = open[i]; });
      }
      const toolbar = html.querySelector('[data-collection-toolbar]');
      const curToolbar = this.querySelector('[data-collection-toolbar]');
      if (toolbar && curToolbar) curToolbar.replaceWith(toolbar);
      if (push) history.pushState({}, '', href);
      if (scroll) this.querySelector('[data-collection-toolbar]')?.scrollIntoView({ block: 'start' });
      const count = this.querySelector('[data-collection-count]');
      const live = document.getElementById('live-region');
      if (count && live) live.textContent = count.textContent;
    } catch (err) {
      if (err.name === 'AbortError') return;
      location.href = href; // fall back to a full navigation
    } finally {
      this.querySelector('[data-collection-results]')?.setAttribute('aria-busy', 'false');
    }
  }
}
if (!customElements.get('collection-page')) customElements.define('collection-page', CollectionPage);
